import useSWR from "swr"
import { useCallback } from "react"
import { hasActiveSession } from "common/lib/cookies"
import { CurrentUserNewsletterList, fetchCurrentUser } from "common/lib/fetchCurrentUser"

const SUBSCRIPTIONS_URL = "/users/current.json?include=newsletters"

type NewsletterList = {
  id: string
  name: string
  pitch: string
  frequency: string
  region: { code: string }
}

const toNewsletterList = ({
  region_code,
  ...newsletterList
}: CurrentUserNewsletterList): NewsletterList => ({
  ...newsletterList,
  id: String(newsletterList.id),
  region: { code: region_code },
})

/**
 * Data hook to return the current viewer newsletter subscriptions data.
 *
 * Newsletter subscriptions are only related to their primary email address.
 */
export const useViewerSubscriptions = () => {
  const { data } = useSWR(hasActiveSession() ? SUBSCRIPTIONS_URL : null, fetchCurrentUser)
  const emails = data?.id === undefined ? undefined : data.emails

  const getPrimaryEmail = useCallback(() => emails?.find((email) => email.primary), [emails])

  const getPrimaryEmailAddress = useCallback(
    () => getPrimaryEmail()?.email || "",
    [getPrimaryEmail],
  )

  // Rails lists only subscribed newsletters, `subscribed` keeps the shape callers expect.
  const getNewsletterSubscriptions = useCallback(
    () =>
      getPrimaryEmail()?.newsletter_lists.map((newsletterList) => ({
        subscribed: true,
        newsletterList: toNewsletterList(newsletterList),
      })) || [],
    [getPrimaryEmail],
  )

  // Go through all the users emails and get all subscribed newsletters from each
  const getAllSubscribedNewsletters = useCallback(() => {
    const newsletterLists = (emails || []).flatMap((email) => email.newsletter_lists)

    return [...new Map(newsletterLists.map((list) => [list.id, toNewsletterList(list)])).values()]
  }, [emails])

  return {
    getNewsletterSubscriptions,
    getPrimaryEmailAddress,
    getAllSubscribedNewsletters,
  }
}
